/* eslint-disable @ngrx/prefer-effect-callback-in-block-statement */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { EMPTY, exhaustMap, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';

import { RoleFacade } from '@cdba/core/auth/role-facade/role.facade';
import {
  getSelectedCalculation,
  getSelectedReferenceTypeIdentifier,
  selectBillOfMaterialsType,
  selectIdentifierOfSelectedItem,
} from '@cdba/core/store/selectors';
import { ProductDetailService } from '@cdba/detail/service/detail.service';
import { BillOfMaterialsType } from '@cdba/shared/constants';

import {
  loadCalculationsSuccess,
  loadCostComponentSplit,
  loadCostComponentSplitFailure,
  loadCostComponentSplitSuccess,
  loadSchaefflerBom,
  loadSchaefflerBomFailure,
  loadSchaefflerBomSuccess,
  loadVitescoBom,
  loadVitescoBomFailure,
  loadVitescoBomSuccess,
  selectBomItem,
  selectCalculation,
} from '../../../actions';

@Injectable()
export class BomEffects {
  private readonly actions$ = inject(Actions);
  private readonly productDetailService = inject(ProductDetailService);
  private readonly store = inject(Store);
  private readonly roleFacade = inject(RoleFacade);

  triggerBomLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(selectCalculation, loadCalculationsSuccess),
      concatLatestFrom(() => this.store.select(selectBillOfMaterialsType)),
      filter(([_action, type]) => type !== undefined),
      exhaustMap(([_action, productType]) => {
        switch (productType) {
          case BillOfMaterialsType.SCHAEFFLER: {
            return this.store.select(getSelectedCalculation).pipe(
              take(1),
              filter((calculation) => !!calculation),
              map((calculation) => {
                const {
                  costingDate,
                  costingNumber,
                  costingType,
                  version,
                  enteredManually,
                  referenceObject,
                  valuationVariant,
                } = calculation;

                return {
                  costingDate,
                  costingNumber,
                  costingType,
                  version,
                  enteredManually,
                  referenceObject,
                  valuationVariant,
                };
              }),
              map((bomIdentifier) => loadSchaefflerBom({ bomIdentifier }))
            );
          }
          case BillOfMaterialsType.VITESCO: {
            return this.store.select(getSelectedReferenceTypeIdentifier).pipe(
              take(1),
              filter((bomIdentifier) => !!bomIdentifier),
              map((bomIdentifier) => loadVitescoBom({ bomIdentifier }))
            );
          }
          default: {
            return EMPTY;
          }
        }
      })
    )
  );

  loadSchaefflerBom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSchaefflerBom),
      concatLatestFrom(() => this.roleFacade.hasAnyPricingRole$),
      exhaustMap(([action, hasPricingRole]) =>
        hasPricingRole
          ? this.productDetailService
              .getSchaefflerBom(action.bomIdentifier)
              .pipe(
                map((items) => loadSchaefflerBomSuccess({ items })),
                catchError((error: HttpErrorResponse) =>
                  of(
                    loadSchaefflerBomFailure({
                      errorMessage: error.error?.detail || error.message,
                      statusCode: error.status,
                    })
                  )
                )
              )
          : of(
              loadSchaefflerBomFailure({
                errorMessage: 'User has no valid cost roles.',
                statusCode: undefined,
              })
            )
      )
    )
  );

  loadVitescoBom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadVitescoBom),
      concatLatestFrom(() => this.roleFacade.hasAnyPricingRole$),
      exhaustMap(([action, hasPricingRole]) =>
        hasPricingRole
          ? this.productDetailService.getVitescoBom(action.bomIdentifier).pipe(
              map((items) => loadVitescoBomSuccess({ items })),
              catchError((error: HttpErrorResponse) =>
                of(
                  loadVitescoBomFailure({
                    errorMessage: error.error?.detail || error.message,
                    statusCode: error.status,
                  })
                )
              )
            )
          : of(
              loadVitescoBomFailure({
                errorMessage: 'User has no valid cost roles.',
                statusCode: undefined,
              })
            )
      )
    )
  );

  triggerLoadOfCostComponentSplit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(selectBomItem, loadSchaefflerBomSuccess),
      concatLatestFrom(() => this.store.select(selectIdentifierOfSelectedItem)),
      filter(([_action, identifier]) => identifier !== undefined),
      mergeMap(([_action, identifier]) => [
        loadCostComponentSplit({ bomIdentifier: identifier }),
      ])
    )
  );

  loadCostComponentSplit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCostComponentSplit),
      exhaustMap((action) =>
        this.productDetailService
          .getCostComponentSplit(action.bomIdentifier)
          .pipe(
            map((items) => loadCostComponentSplitSuccess({ items })),
            catchError((error: HttpErrorResponse) =>
              of(
                loadCostComponentSplitFailure({
                  errorMessage: error.error?.detail || error.message,
                  statusCode: error.status,
                })
              )
            )
          )
      )
    )
  );
}
